import classnames from 'classnames';
import { map, join, omit, round, includes } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import AspectRatio from 'components/AspectRatio';
import { IMAGE_HOST as CONTENTFUL_IMAGE_HOST } from 'constants/contentful';
import { LAZY_LOAD_CLASSNAME } from 'constants/lazyLoading';
import CloudinaryRemoteFetchImage from 'models/CloudinaryRemoteFetchImage';
import Cloudinary from 'utils/Cloudinary';

class Image extends React.Component {
  static get downscaleMinWidth() {
    return 40;
  }

  static get downscaleSliceCount() {
    return 15;
  }

  get downscaleSliceSize() {
    return this.props.width / Image.downscaleSliceCount;
  }

  get responsiveWidths() {
    let maxWidth = this.props.width;
    const responsiveWidths = [];

    if (maxWidth < Image.downscaleMinWidth) {
      responsiveWidths.push(round(maxWidth, 0));
      return responsiveWidths;
    }

    while (maxWidth >= Image.downscaleMinWidth) {
      responsiveWidths.push(round(maxWidth, 0));
      maxWidth -= this.downscaleSliceSize;
    }

    return responsiveWidths;
  }

  get height() {
    if (!this.props.ratio) {
      return this.props.height;
    }
    const [ratioWidth, ratioHeight] = this.props.ratio.split(':').map(Number);
    return Math.round((this.props.width / ratioWidth) * ratioHeight);
  }

  get srcSet() {
    return join(
      map(
        this.responsiveWidths,
        responsiveWidth =>
          `${this.buildSrc({
            width: responsiveWidth,
            ...this.props.transformations,
          })} ${responsiveWidth}w`
      ),
      ','
    );
  }

  get src() {
    return this.buildSrc({
      width: this.props.width,
      ...this.props.transformations,
    });
  }

  get imageTag() {
    const classes = classnames(
      'ImageV2',
      {
        'ImageV2--object-fit-cover': this.props.coverParentNode,
      },
      LAZY_LOAD_CLASSNAME,
      this.props.className
    );

    const remainingProps = omit(this.props, [
      'src',
      'width',
      'height',
      'ratio',
      'alt',
      'transformations',
      'sizes',
      'coverParentNode',
      'className',
    ]);

    return (
      <>
        <img
          data-src={this.src}
          data-srcset={this.srcSet}
          data-sizes="auto"
          alt={this.props.alt}
          className={classes}
          {...remainingProps}
        />

        <style jsx>
          {`
            .ImageV2 {
              display: block;
              width: 100%;
              max-width: 100%;
              color: transparent;
            }

            .ImageV2--object-fit-cover {
              min-width: 100%;
              min-height: 100%;
              max-height: 100%;
              object-fit: cover;
            }
          `}
        </style>
      </>
    );
  }

  buildSrc(transformations) {
    const { src } = this.props;
    if (includes(src, CONTENTFUL_IMAGE_HOST)) {
      return Cloudinary.buildAutoUploadUrl(src, transformations);
    }
    const image = new CloudinaryRemoteFetchImage(src, transformations);
    return image.src;
  }

  render() {
    return this.props.coverParentNode ? (
      this.imageTag
    ) : (
      <AspectRatio height={this.height} width={this.props.width}>
        {this.imageTag}
      </AspectRatio>
    );
  }
}

Image.propTypes = {
  alt: PropTypes.string,
  className: PropTypes.string,
  coverParentNode: PropTypes.bool,
  height: PropTypes.number,
  ratio: PropTypes.string,
  src: PropTypes.string.isRequired,
  transformations: PropTypes.shape({
    backgroundColor: PropTypes.string,
    format: PropTypes.string,
    devicePixelRatio: PropTypes.number,
    quality: PropTypes.string,
  }),
  width: PropTypes.number.isRequired,
};

Image.defaultProps = {
  alt: '',
  className: '',
  height: undefined,
  coverParentNode: false,
  ratio: '',
  transformations: {
    ...Image.defaultTransformations,
  },
};

export default Image;
